import {endOfDay, format, isAfter, isBefore, startOfDay, subYears} from 'date-fns'

/**
 * Utility functions for date parsing and formatting.
 * @param {string} dateString - A date string in the format "YYYY-MM-DD".
 * @returns {Date|null} - Returns a Date object if the input string is valid, otherwise returns null.
 */
export const parseBackendDate = (dateString) => {
    if (!dateString) {
        return null
    }

    // Create date in local timezone to avoid UTC conversion issues
    const [year, month, day] = dateString.split('-').map((n) => Number.parseInt(n, 10))
    // check if the date is valid
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return null
    }
    if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1 || day > 31) {
        return null
    }
    // Month is 0-indexed in JavaScript Date, so we subtract 1 from month
    return new Date(year, month - 1, day)
}

/**
 * Formats a Date object to a string in the "YYYY-MM-DD" format.
 * @param {Date|null} date - A Date object to format.
 * @returns {string|null} - Returns a string in the format "YYYY-MM-DD" if the input date is valid, otherwise returns null.
 */
export const formatBackendDate = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return null
    }
    // Convert the selected Date object to a local "YYYY-MM-DD" string,
    // to avoid timezone-related shifts (e.g. off-by-one day)
    return format(date, 'yyyy-MM-dd')
}

/**
 * Resolves the selectable range of the date picker: the last 100 years up to today unless given.
 *
 * @param {Date} [minDate]
 * @param {Date} [maxDate]
 * @returns {{minDate: Date, maxDate: Date}}
 */
export const getDateRange = (minDate, maxDate) => {
    const today = new Date()
    return {minDate: minDate ?? subYears(today, 100), maxDate: maxDate ?? today}
}

/**
 * Whether the day of `date` lies within `minDate` .. `maxDate`, both days included.
 *
 * @param {Date} date
 * @param {Date} minDate
 * @param {Date} maxDate
 * @returns {boolean}
 */
export const isDateInRange = (date, minDate, maxDate) =>
    !isBefore(date, startOfDay(minDate)) && !isAfter(date, endOfDay(maxDate))
